/**
 * Workspace — the public workspace card (workspaces/{orgId}) the storefront
 * renders before any sign-in. Pure state; workspace.sync.ts (web-side) is
 * the sole writer, folding the live Firestore doc it reads through
 * workspace.api.ts.
 *
 * Also owns the identity write-intent channel: organization-settings.view.ts
 * (web) composes a WorkspaceIdentityAction and pushes it to
 * workspaceIdentityAction$; workspace.sync.ts (web) is the sole subscriber,
 * settling whichever of the brand's Storage objects the save carries — the
 * storefront logo and the wallet-pass variant beside it, neither of them on a
 * save that touches only text — then writing brand/legal/email/site to
 * workspaces/{orgId} via workspace.api.ts, never flags or payment (the
 * Integrations card owns payment). That order is what keeps a rejected
 * upload from landing a patch whose assets never arrived.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { walletInkPolarity } from "@momo/ticket-template/wallet";
import type { TicketTemplateVariant, Workspace } from "@momo/types";
import { Subject } from "rxjs";
import { activeActor } from "../auth/actor.state.ts";
import { organizationId } from "./organization.state.ts";

export type WorkspaceCardState = {
	/** `none`: no organization is open (the bare host before a sign-in picks
	 *  one), so there is no card to wait for. */
	readonly status: "loading" | "none" | "not-found" | "ready";
	readonly workspace: Workspace.Workspace | null;
	/** The organization this answer was read under, so a reader keyed on the
	 *  organization takes only that organization's card. */
	readonly orgId?: string;
};

const initial: WorkspaceCardState = { status: "loading", workspace: null };

// keepOnClear: the card is public and identity-free — every visitor, signed
// in or anonymous, sees the same document — so an identity change (sign-out,
// a different member signing in) must not reset it. Its sole writer is a
// live Firestore snapshot listener (workspace.sync.ts) that only re-emits
// when the doc itself changes; clearStates() wiping it to `initial` would
// strand it there forever since nothing re-writes an unchanged doc.
export const workspaceCard = state("workspace/card").memory(initial, {
	keepOnClear: true,
});

// A primary domain is usable only after its active claim answers for this
// organization. Retaining the answer's scope prevents a switch showing an old
// organization's copied order or wallet address.
export const workspaceAddress = state<{
	nativeOrigin: string | null;
	orgId: string | null;
	origin: string | null;
}>("workspace/address").memory(
	{ nativeOrigin: null, orgId: null, origin: null },
	{ keepOnClear: true },
);

export const platformOrigin = computed(
	() =>
		`${workspaceAddress.value.nativeOrigin ?? window.location.origin}${organizationId.value ? `/o/${encodeURIComponent(organizationId.value)}` : ""}`,
);
export const workspaceOrigin = computed(() =>
	workspaceAddress.value.orgId === organizationId.value
		? (workspaceAddress.value.origin ?? platformOrigin.get())
		: platformOrigin.get(),
);
export const workspaceDomain = computed(
	() => new URL(workspaceOrigin.get()).host,
);
export const workspaceMapsApiKey = computed(
	() => workspaceCard.value.workspace?.site?.mapsApiKey ?? null,
);

// The one hex `<schmancy-theme>` expands into a full M3 palette — null until
// the workspace card resolves with a brand seed, or for any workspace that
// never set one (a fresh fork, this deployment's own designer fixture).
export const brandSeedColor = computed(
	() => workspaceCard.value.workspace?.brand.colors?.seed ?? null,
);

// Every paint site (theme init, map markers) needs a real hex, never null —
// binding a nullish value to a persisted schmancy property serializes the
// string "undefined"/"null" and corrupts it on the next load. #6200ee is
// schmancy's own Material baseline seed, not a deployment value.
export const effectiveBrandSeedColor = computed(
	() => brandSeedColor.get() ?? "#6200ee",
);

// The storefront wordmark — null until the workspace card resolves, or for
// any workspace that never set a logo asset.
export const brandLogo = computed(
	() => workspaceCard.value.workspace?.brand.logo ?? null,
);

// The workspace's display name, falling back to the generic product name
// before the card resolves or for a workspace that never set one.
export const brandName = computed(
	// catalog-text-ok: "Tickets" stands in the wordmark slot for a workspace with no brand name of its own, so it is a name and reads the same in both languages
	() => workspaceCard.value.workspace?.brand.name ?? "Tickets",
);

// True only while the card's first read is still in flight — false once it
// resolves (ready, not-found, or none open at all), so a logo-less brand-logo.view
// can distinguish "still loading" from "resolved with nothing to show".
export const brandLoading = computed(
	() => workspaceCard.value.status === "loading",
);

// No organization is open, so there is no brand to show: a surface that would
// carry one names its own job instead.
export const brandAbsent = computed(
	() => workspaceCard.value.status === "none",
);

// The open organization's name, null on the bare host: no organization is
// open there, so a surface drops the name rather than print a placeholder.
export const openBrandName = computed(() =>
	brandAbsent.get() ? null : brandName.get(),
);

/** Whether the signed-in actor may edit the organization's identity —
 *  mirrors workspace.entity.ts's Workspace.update rule (tickets.events.manage
 *  + mfa). mfa isn't re-checked here: sessionReady already requires it to
 *  reach any admin route, so every actor on this page already carries it.
 *  Mirrors events.state.ts's own canManageEvents for the same permission. */
export const canManageOrganization = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.events.manage") ?? false,
);

// ── identity write-intent channel — see the module doc above. ──────────────

/**
 * `walletMark` on a "set" is the operator's own answer to what the pass gets:
 * "derive" a light variant from their file, or their file "verbatim". It rides
 * the intent rather than being decided by the writer because a coloured or
 * two-tone mark loses brand information when whitened, so nothing but an
 * explicit choice may authorise it. "deriveWalletMark" keeps the stored logo
 * untouched and produces only the pass variant — the one route by which a
 * workspace provisioned from the seam, whose logo is a `data:` URI with no
 * Storage object behind it at all, ever acquires one.
 */
export type WorkspaceLogoIntent =
	| { readonly kind: "keep" }
	| {
			readonly kind: "set";
			readonly blob: Blob;
			readonly walletMark: "derive" | "verbatim";
	  }
	| { readonly kind: "deriveWalletMark" }
	| { readonly kind: "remove" };

export type WorkspaceIdentityAction = {
	readonly requestId: string;
	readonly identity: Pick<
		Workspace.Workspace,
		"brand" | "legal" | "email" | "site"
	>;
	readonly logo: WorkspaceLogoIntent;
};
export const workspaceIdentityAction$ = new Subject<WorkspaceIdentityAction>();

/** `warning` carries a save that landed while a part of it did not — the
 *  operator's brand/legal/email/site patch committed, but the pass variant they
 *  asked for was not made. Distinct from `ok: false`, which means nothing
 *  landed and the form should be retried whole. */
export type WorkspaceIdentityResult =
	| {
			readonly requestId: string;
			readonly ok: true;
			readonly warning: string | null;
	  }
	| { readonly requestId: string; readonly ok: false; readonly error: string };
export const workspaceIdentityResult$ = new Subject<WorkspaceIdentityResult>();

/** `brand/wallet-logo.png` — the light variant a wallet pass composites over
 *  its own dark ground — as the pass will find it: its fetchable `uri`, or
 *  `null` where no such object exists. No field on the card records either, so
 *  the object itself is the only authoritative answer; workspace.sync.ts (web)
 *  probes for it and re-probes after each save. `status` stays "unknown" until
 *  that first answer lands, so a slow or failed probe reads as silence rather
 *  than as an absence the operator is asked to act on. */
export type WalletMarkState = {
	readonly status: "unknown" | "present" | "absent";
	readonly uri: string | null;
};

const initialWalletMark: WalletMarkState = { status: "unknown", uri: null };

// keepOnClear, for workspaceCard's own reason: the object is public (`allow
// get: if true`) and its existence is identity-free, while the probe that
// answers for it runs at boot and after a save and at no other moment. A
// clearStates() on sign-in would reset this to "unknown" with nothing left to
// re-ask, and the operator who just signed in to make a mark would never be
// told one is missing.
export const walletMark = state("workspace/walletMark").memory(
	initialWalletMark,
	{ keepOnClear: true },
);

/** The saved logo has no pass variant behind it. Settings' offer to make one
 *  hangs on this — without that offer no workspace provisioned from the seam
 *  ever gets a mark, since its logo never passed through the picker. */
export const walletMarkMissing = computed(
	() =>
		workspaceCard.value.workspace?.brand.logo != null &&
		walletMark.value.status === "absent",
);

/** The mark a wallet pass draws, for a surface that has to show one — read
 *  off the pass ground's own ink polarity, the same `walletInkPolarity` the
 *  composer's candidate ladder is ordered by, so the preview and the signed
 *  pass cannot name different files.
 *
 *  A ground taking LIGHT ink is dark, and takes the derived variant wherever
 *  it exists — the composer's first candidate and the whole point of deriving
 *  it — with the storefront asset only where it does not. A ground taking DARK
 *  ink is light, and the derived variant is whitened by contract, so it is
 *  never a candidate there and the storefront asset is the only one.
 *
 *  That storefront fallback is an APPROXIMATION and the only one available
 *  here: the composer's remaining candidates are `logo.{png,jpg,jpeg}` in
 *  Storage, which the browser can only learn by probing three more names on a
 *  path every anonymous buyer boots. So it stands in for them, and diverges in
 *  the two cases they answer differently — a webp mark, which the composer
 *  refuses and the storefront renders, and a seam-provisioned `data:` logo,
 *  which has no Storage object behind it at all. On a dark ground both resolve
 *  the moment a pass variant exists, which is what settings exists to prompt.
 *
 *  A parameter is what keeps this one derivation rather than three: `computed`
 *  memoises over signals alone and takes no argument, and the variant is the
 *  caller's, not a signal's. */
export function walletPassMarkUri(
	variant: TicketTemplateVariant,
): string | null {
	if (walletInkPolarity(brandSeedColor.get(), variant) === "dark")
		return brandLogo.get();
	return walletMark.value.uri ?? brandLogo.get();
}
